import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Share2, MessageCircle, Twitter, Facebook, Link2 } from "lucide-react";

export const CONTENT_STATUS_LABELS = {
  bajo_investigacion: "Bajo investigación",
  evidencia_preliminar: "Evidencia preliminar",
  confirmado: "Confirmado documentalmente",
  esperando_respuesta: "Esperando respuesta",
  referido: "Referido a autoridades",
  resuelto: "Resuelto",
  sin_evidencia: "Sin evidencia suficiente",
  corregido: "Corregido o actualizado",
};

export const PROMISE_LABELS = {
  cumplida: "Cumplida",
  parcialmente_cumplida: "Parcialmente cumplida",
  en_progreso: "En progreso",
  estancada: "Estancada",
  incumplida: "Incumplida",
  abandonada: "Abandonada",
  no_verificable: "No verificable",
};

const PROMISE_COLORS = {
  cumplida: "bg-cyan-500 text-white",
  parcialmente_cumplida: "bg-cyan-100 text-cyan-800",
  en_progreso: "bg-slate-200 text-slate-700",
  estancada: "bg-amber-100 text-amber-800",
  incumplida: "bg-rose-500 text-white",
  abandonada: "bg-rose-100 text-rose-800",
  no_verificable: "bg-slate-100 text-slate-500",
};

export function PromiseBadge({ status }) {
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${PROMISE_COLORS[status] || "bg-slate-200 text-slate-700"}`}
      data-testid={`promise-badge-${status}`}>
      {PROMISE_LABELS[status] || status}
    </span>
  );
}

export function StatusBadge({ status }) {
  return (
    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
      data-testid={`status-badge-${status}`}>
      {CONTENT_STATUS_LABELS[status] || status}
    </span>
  );
}

export function TypeTag({ type }) {
  const map = {
    investigacion: { label: "Investigación", cls: "bg-slate-900 text-white" },
    analisis: { label: "Análisis", cls: "bg-cyan-100 text-cyan-800" },
    opinion: { label: "Opinión", cls: "bg-amber-100 text-amber-800" },
  };
  const t = map[type] || map.investigacion;
  return <span className={`inline-block px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${t.cls}`}>{t.label}</span>;
}

export function SectionLabel({ children }) {
  return <p className="text-xs tracking-[0.2em] uppercase text-cyan-600 font-semibold mb-3">{children}</p>;
}

export function Loading({ label = "Cargando…" }) {
  return (
    <div className="flex items-center justify-center py-20" data-testid="loading">
      <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mr-3" />
      <span className="text-slate-500">{label}</span>
    </div>
  );
}

export function Empty({ label = "No hay contenido disponible todavía." }) {
  return <div className="text-center py-16 text-slate-400" data-testid="empty-state">{label}</div>;
}

export function PageHero({ label, title, subtitle, image }) {
  return (
    <div className="relative bg-slate-900 overflow-hidden">
      {image && (
        <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      )}
      <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20">
        {label && <p className="text-xs tracking-[0.2em] uppercase text-cyan-400 font-semibold mb-4">{label}</p>}
        <h1 className="font-heading font-black text-3xl md:text-5xl text-white tracking-tight leading-none max-w-3xl">{title}</h1>
        {subtitle && <p className="text-slate-300 mt-5 max-w-2xl text-lg leading-relaxed">{subtitle}</p>}
      </div>
    </div>
  );
}

export function fmtMoney(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("es-PR", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
}

export function Crumbs({ items }) {
  return (
    <nav className="text-sm text-slate-500 mb-6 flex items-center gap-2 flex-wrap" data-testid="breadcrumbs">
      {items.map((it, i) => (
        <span key={it.label} className="flex items-center gap-2">
          {it.to ? <Link to={it.to} className="hover:text-cyan-600 transition-colors">{it.label}</Link> : <span className="text-slate-700">{it.label}</span>}
          {i < items.length - 1 && <span className="text-slate-300">/</span>}
        </span>
      ))}
    </nav>
  );
}

export function ShareMenu({ title = "", path, label = "Compartir", align = "right", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev) => { if (ref.current && !ref.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const here = typeof window !== "undefined" ? window.location.pathname : "";
  const url = `${origin}${path || here}`;
  const text = title ? `2Voto: ${title}` : "2Voto — infraestructura cívica";
  const links = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };
  const openNet = (n) => { window.open(links[n], "_blank", "noopener,noreferrer"); setOpen(false); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado."); }
    catch { toast.error("No se pudo copiar el enlace."); }
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid="share-menu-button" aria-haspopup="true" aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-cyan-300 hover:text-cyan-600 transition-colors">
        <Share2 className="w-4 h-4" /> {label}
      </button>
      {open && (
        <div data-testid="share-menu" className={`absolute ${align === "right" ? "right-0" : "left-0"} z-30 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl`}>
          <button onClick={() => openNet("whatsapp")} data-testid="share-whatsapp"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
            <MessageCircle className="w-4 h-4 text-emerald-500" /> WhatsApp
          </button>
          <button onClick={() => openNet("x")} data-testid="share-x"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
            <Twitter className="w-4 h-4 text-slate-900" /> X (Twitter)
          </button>
          <button onClick={() => openNet("facebook")} data-testid="share-facebook"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">
            <Facebook className="w-4 h-4 text-blue-600" /> Facebook
          </button>
          <button onClick={copy} data-testid="share-copy"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors">
            <Link2 className="w-4 h-4 text-cyan-500" /> Copiar enlace
          </button>
        </div>
      )}
    </div>
  );
}
